import { FormProvider } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { FormRow } from '@gravity-ui/components';
import { CircleQuestion, PersonPencil, ArrowRightFromSquare } from '@gravity-ui/icons';
import { Button, Checkbox, Icon, Link, Select, Sheet, TextInput, Tooltip } from '@gravity-ui/uikit';

import type { Establishment, Region } from 'store/api/private/user-api/types';
import { FileChange, Loader, ProfileError } from './utils';
import { usePhotoUpload } from './hooks/usePhotoUpload';
import { useProfileForm } from './hooks/useProfileForm';
import { DocumentModal } from './utils/modals';
import PdfHandler from './utils/pdf-handler';
import { ModalOut } from 'features/ui-kit/modal-log-out';
import { useDispatch } from 'react-redux';
import { useNavigateReplace } from 'hooks';
import { useCallback, useState } from 'react';
import { logout } from 'store/slices/user-slice';
import { routes } from 'routes';
import { useMediaQuery } from 'react-responsive';
import { DocumentSheet, SheetOut } from './utils/sheets';
import { ProfileIcon } from 'features/ui-kit/profile-icon';
import {
	AGREEMENT,
	CHOOSE_INSTITUTE,
	CHOOSE_REGION,
	EMAIL,
	EMAIL_LABEL,
	EMAIL_TOOLTIP,
	EXIT,
	FAMILY_NAME,
	FAMILY_NAME_PLACEHOLDER,
	GAME,
	IMG_TEXT,
	INSTITUTE_TOOLTIP,
	INSTITUTION,
	NAME,
	NAME_PLACEHOLDER,
	PERSONAL_DATA,
	POLICY,
	PRIVACY,
	PRIVACY_POLICY,
	REGION,
	SAVE,
	SPECIALIZATION,
	SPECIALIZATION_PLACEHOLDER,
	SURNAME,
	SURNAME_PLACEHOLDER,
	TITLE,
} from './constants';

import styles from './styles.module.scss';

export const Profile = () => {
	const {
		userData,
		methods,
		isLoading,
		error,
		regions,
		establishments,
		handleSubmit,
		errors,
		isSubmitted,
		setValue,
	} = useProfileForm();
	const { imageSrc, handleImageClick, controlProps, triggerProps } = usePhotoUpload(userData!);
	const dispatch = useDispatch();
	const navigate = useNavigateReplace();
	const [visible, setVisible] = useState(false);

	const isMobile = useMediaQuery({ query: '(max-width: 515px)' });

	const handleLogOut = useCallback(() => {
		dispatch(logout());
		navigate(routes.login.path);
	}, [dispatch, navigate]);

	const { register } = methods;
	const {
		pdfUrlPolicy,
		pdfUrlUserAgreement,
		pdfPersonalDataAgreement,
		openModal,
		openModalPolicy,
		openModalPersonalDataAgreement,
		handleOpenModal,
		handleOpenModalPrivacy,
		handleOpenModalPersonal,
		handleCloseModal,
		handleCloseModalPrivacy,
		handleCloseModalPersonal,
	} = PdfHandler();

	return (
		<div className={styles.profile}>
			{isMobile ? (
				<div className={styles.profile__title}>
					<ProfileIcon />
					<h2>{TITLE}</h2>
				</div>
			) : (
				<h2>{TITLE}</h2>
			)}

			{isLoading && <Loader />}

			{!isLoading && error && <ProfileError />}

			{!isLoading && userData && (
				<div className={styles.profile__container}>
					<div className={styles.profile__img}>
						{imageSrc ? (
							<div className={styles.profile__img__wrapper}>
								<img
									src={imageSrc}
									alt="Profile"
									className={styles.profile__img__content}
									onClick={handleImageClick}
								/>
								<FileChange
									controlProps={{ ...controlProps, id: 'file-input' }}
									triggerProps={triggerProps}
									imageSrc={imageSrc}
								/>
							</div>
						) : (
							<>
								<PersonPencil />
								<FileChange
									controlProps={{ ...controlProps, id: 'file-input' }}
									triggerProps={triggerProps}
								/>
								<p>{IMG_TEXT}</p>
							</>
						)}
					</div>
					<FormProvider {...methods}>
						<div className={styles.profile__form}>
							<form onSubmit={handleSubmit}>
								<div className={styles.profile__inputs}>
									<FormRow
										label={EMAIL_LABEL}
										className={`${styles.profile__inputs__row} ${styles.profile__inputs__adaptive_tooltip}`}
										labelHelpPopover={
											<Tooltip
												contentClassName={styles.profile__inputs__row__tooltip}
												closeDelay={1500}
												content={
													<span>
														<div>{EMAIL_TOOLTIP} </div>{' '}
														<Link view="normal" href="#">
															{EMAIL}
														</Link>
													</span>
												}
											>
												<div tabIndex={0}>
													<CircleQuestion />
												</div>
											</Tooltip>
										}
									>
										<TextInput
											size="xl"
											disabled
											{...register('email', { required: true })}
											validationState={isSubmitted && errors.email ? 'invalid' : undefined}
											errorPlacement="inside"
											errorMessage={
												isSubmitted && errors.email ? 'Обязательно для заполнения' : undefined
											}
										/>
									</FormRow>

									<FormRow label={FAMILY_NAME} required className={styles.profile__inputs__row}>
										<TextInput
											placeholder={FAMILY_NAME_PLACEHOLDER}
											size="xl"
											hasClear
											type="text"
											{...register('lastName', { required: true })}
											validationState={isSubmitted && errors.lastName ? 'invalid' : undefined}
											errorPlacement="inside"
											errorMessage={
												isSubmitted && errors.lastName
													? 'Обязательно для заполнения'
													: undefined
											}
										/>
									</FormRow>

									<FormRow label={NAME} required className={styles.profile__inputs__row}>
										<TextInput
											placeholder={NAME_PLACEHOLDER}
											size="xl"
											hasClear
											type="text"
											{...register('firstName', { required: true })}
											validationState={isSubmitted && errors.firstName ? 'invalid' : undefined}
											errorPlacement="inside"
											errorMessage={
												isSubmitted && errors.firstName
													? 'Обязательно для заполнения'
													: undefined
											}
										/>
									</FormRow>

									<FormRow label={SURNAME} required className={styles.profile__inputs__row}>
										<TextInput
											placeholder={SURNAME_PLACEHOLDER}
											size="xl"
											hasClear
											type="text"
											{...register('middleName', { required: true })}
											validationState={isSubmitted && errors.middleName ? 'invalid' : undefined}
											errorPlacement="inside"
											errorMessage={
												isSubmitted && errors.middleName
													? 'Обязательно для заполнения'
													: undefined
											}
										/>
									</FormRow>

									<FormRow label={REGION} required className={styles.profile__inputs__row}>
										<Select
											hasClear
											size="xl"
											filterable
											placeholder={CHOOSE_REGION}
											className={styles.profile__inputs__row__select}
											{...register('region', { required: true })}
											defaultValue={userData?.region?.fullname ? [userData.region.fullname] : []}
											onUpdate={(value) => {
												setValue('region', value ? value[0] : '');
											}}
											validationState={isSubmitted && errors.region ? 'invalid' : undefined}
											errorPlacement="inside"
											errorMessage={
												isSubmitted && errors.region ? 'Обязательно для заполнения' : undefined
											}
										>
											{Array.isArray(regions) && regions.length > 0
												? regions.map((region: Region) => (
														<Select.Option key={region.guid} value={region.fullname}>
															{region.fullname}
														</Select.Option>
													))
												: []}
										</Select>
									</FormRow>

									<FormRow
										label={INSTITUTION}
										className={`${styles.profile__inputs__row}  ${styles.profile__inputs__adaptive_tooltip}`}
										labelHelpPopover={
											<Tooltip
												contentClassName={styles.profile__inputs__row__tooltip}
												closeDelay={1500}
												content={
													<span>
														<span>{INSTITUTE_TOOLTIP}</span>{' '}
														<Link view="normal" href="#">
															{EMAIL}
														</Link>
													</span>
												}
											>
												<div tabIndex={0}>
													<CircleQuestion />
												</div>
											</Tooltip>
										}
									>
										<Select
											hasClear
											size="xl"
											filterable
											placeholder={CHOOSE_INSTITUTE}
											className={styles.profile__inputs__row__select}
											defaultValue={
												userData?.establishment?.name ? [userData.establishment.name] : []
											}
											onUpdate={(value) => {
												setValue('establishment', value ? value[0] : '');
											}}
											{...register('establishment', { required: true })}
											validationState={
												isSubmitted && errors.establishment ? 'invalid' : undefined
											}
											errorPlacement="inside"
											errorMessage={
												isSubmitted && errors.establishment
													? 'Обязательно для заполнения'
													: undefined
											}
										>
											{Array.isArray(establishments) && establishments.length > 0
												? establishments.map((establishment: Establishment) => (
														<Select.Option
															key={establishment.name}
															value={establishment.name}
														>
															{establishment.name}
														</Select.Option>
													))
												: []}
										</Select>
									</FormRow>

									<FormRow
										label={SPECIALIZATION}
										required
										className={`${styles.profile__inputs__row} ${styles.top}`}
									>
										<TextInput
											placeholder={SPECIALIZATION_PLACEHOLDER}
											size="xl"
											hasClear
											{...register('specialty', { required: true })}
											validationState={isSubmitted && errors.specialty ? 'invalid' : undefined}
											errorPlacement="inside"
											errorMessage={
												isSubmitted && errors.specialty
													? 'Обязательно для заполнения'
													: undefined
											}
										/>
									</FormRow>
								</div>
								<Controller
									name="userAgreement"
									control={methods.control}
									rules={{ required: true }}
									render={({ field }) => {
										return (
											<Checkbox
												checked={field.value}
												onChange={(e) => {
													field.onChange(e.target ? e.target.checked : e);
												}}
												content={
													<span>
														{PRIVACY}{' '}
														<Link
															view="normal"
															href="#"
															onClick={handleOpenModal}
															className={styles.link}
														>
															{AGREEMENT}
														</Link>
													</span>
												}
												size="l"
												className={styles.checkbox}
											/>
										);
									}}
								/>
								{isMobile ? (
									<DocumentSheet
										open={openModal}
										onClose={handleCloseModal}
										pdfSrc={pdfUrlUserAgreement}
									/>
								) : (
									<DocumentModal
										open={openModal}
										onClose={handleCloseModal}
										pdfSrc={pdfUrlUserAgreement}
									/>
								)}

								<Controller
									name="personalDataAgreement"
									control={methods.control}
									rules={{ required: true }}
									render={({ field }) => {
										return (
											<Checkbox
												checked={field.value}
												onChange={(e) => {
													field.onChange(e.target ? e.target.checked : e);
												}}
												content={
													<span>
														{POLICY}{' '}
														<Link
															view="normal"
															href="#"
															onClick={handleOpenModalPrivacy}
															className={styles.link}
														>
															{PRIVACY_POLICY}
														</Link>{' '}
														{GAME}{' '}
														<Link
															view="normal"
															href="#"
															onClick={handleOpenModalPersonal}
															className={styles.link}
														>
															{PERSONAL_DATA}
														</Link>
													</span>
												}
												size="l"
												className={styles.checkbox}
											/>
										);
									}}
								/>
								{isMobile ? (
									<>
										<DocumentSheet
											open={openModalPolicy}
											onClose={handleCloseModalPrivacy}
											pdfSrc={pdfUrlPolicy}
										/>
										<DocumentSheet
											open={openModalPersonalDataAgreement}
											onClose={handleCloseModalPersonal}
											pdfSrc={pdfPersonalDataAgreement}
										/>
									</>
								) : (
									<>
										<DocumentModal
											open={openModalPolicy}
											onClose={handleCloseModalPrivacy}
											pdfSrc={pdfUrlPolicy}
										/>
										<DocumentModal
											open={openModalPersonalDataAgreement}
											onClose={handleCloseModalPersonal}
											pdfSrc={pdfPersonalDataAgreement}
										/>
									</>
								)}

								{isMobile ? (
									<div className={styles.profile__form__btns}>
										<Button
											size="xl"
											className={styles.profile__form__btns__btn}
											type="submit"
											// disabled={!isValid}
											// view={isValid ? 'action' : 'raised'}
											loading={isLoading}
										>
											{SAVE}
										</Button>
										<Button
											size="xl"
											className={styles.profile__form__btns__btn_exit}
											loading={isLoading}
											view="outlined"
											onClick={() => setVisible(true)}
										>
											<Icon data={ArrowRightFromSquare} size={18} />
											{EXIT}
										</Button>
										<SheetOut
											visible={visible}
											setVisible={setVisible}
											handleLogOut={handleLogOut}
										/>
									</div>
								) : (
									<Button
										size="xl"
										className={styles.profile__form__btns__btn}
										type="submit"
										// disabled={!isValid}
										// view={isValid ? 'action' : 'raised'}
										loading={isLoading}
									>
										{SAVE}
									</Button>
								)}
							</form>
						</div>
					</FormProvider>
				</div>
			)}
		</div>
	);
};
